import React from "react";
import { Table } from "reactstrap";
import toast from "react-hot-toast";
import moment from "moment";
import Api from "services/api";
import { usePermissions } from "layouts/Admin";
import FullPageLoader from "components/common/FullPageLoader";
import { SubmitButton } from "components/common/buttons/SubmitButton";

const COLUMNS = [
  ["interpreter", "Interpreter"],
  ["requestor", "Requestor"],
  ["job_id", "Job ID"],
  ["job_date", "Job Date"],
  ["job_notes", "Job Notes"],
  ["create_date", "Create Date"],
  ["pay_rate", "Pay Rate"],
  ["location_name", "Location Name"],
  ["court_name", "Court Name"],
  ["language_name", "Language Name"],
  ["case_type_name", "Case Type Name"],
  ["holiday", "Holiday"],
  ["travel", "Travel"],
  ["job_count", "Job Count"],
  ["case_count", "Case Count"],
  ["case_numbers", "Case Numbers"],
  ["hours", "Hours"],
  ["holiday_amt", "Holiday Amt."],
  ["travel_amt", "Travel Amt."],
  ["cost", "Cost"],
];

const RawDataReport = () => {
  const { checkPermission } = usePermissions();
  const [startDate, setStartDate] = React.useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = React.useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [rows, setRows] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);

  const params = { start_date: startDate, end_date: endDate };

  const validateRange = () => {
    if (!startDate || !endDate) {
      toast.error("Please select a start and end date.", { className: "errorToast" });
      return false;
    }
    if (moment(endDate).isBefore(moment(startDate))) {
      toast.error("End date must be after the start date.", { className: "errorToast" });
      return false;
    }
    if (moment(endDate).isAfter(moment(startDate).add(6, "months"))) {
      toast.error("Date range cannot exceed 6 months.", { className: "errorToast" });
      return false;
    }
    return true;
  };

  const handleGenerate = async () => {
    if (!validateRange()) return;
    setLoading(true);
    const res = await Api.fetchRawDataReport(params);
    setLoading(false);
    if (res?.status >= 200 && res?.status < 300) {
      setRows(res?.data?.data || []);
    } else {
      toast.error(res?.data?.errors?.end_date?.[0] || res?.data?.message || "Could not generate the report.", { className: "errorToast" });
    }
  };

  const handleDownload = async () => {
    if (!validateRange()) return;
    setDownloading(true);
    const res = await Api.downloadRawDataReport(params);
    setDownloading(false);
    if (res?.status >= 200 && res?.status < 300) {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "RawData.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } else {
      toast.error("Could not download the report.", { className: "errorToast" });
    }
  };

  return (
    <div className="content">
      {(loading || downloading) && <FullPageLoader />}
      <div className="row_main_head">
        <h2 className="main_heading">Raw Data Report</h2>
      </div>

      <div className="card_table p-4">
        <div className="row-primary row-r" style={{ alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div className="date row-primary">
            <h2>Start Date *</h2>
            <div className="input-image">
              <input type="date" className="input-in type_inp" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
          </div>
          <div className="date row-primary">
            <h2>End Date *</h2>
            <div className="input-image">
              <input type="date" className="input-in type_inp" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <SubmitButton buttonType="button" btnText="Generate" handleSubmit={handleGenerate} loader={loading} />
            {checkPermission("Download Raw Data Report") && rows !== null && rows.length > 0 && (
              <SubmitButton buttonType="button" className="green" btnText="Download XLSX" handleSubmit={handleDownload} loader={downloading} />
            )}
          </div>
        </div>
      </div>

      {rows !== null && (
        <div className="card_table p-4">
          <h2 className="main_heading2 mb-2">
            Raw Data between {moment(startDate).format("M/D/YYYY")} and {moment(endDate).format("M/D/YYYY")} — {rows.length} job{rows.length === 1 ? "" : "s"}
          </h2>
          {rows.length === 0 ? (
            <p style={{ color: "#809FB8" }}>No jobs found for this date range.</p>
          ) : (
            <div className="table_responsive" style={{ overflowX: "auto" }}>
              <Table style={{ minWidth: 1800 }}>
                <thead className="text-primary">
                  <tr>
                    {COLUMNS.map(([key, label]) => (
                      <th key={key} style={{ whiteSpace: "nowrap" }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.job_id || i}>
                      {COLUMNS.map(([key]) => (
                        <td key={key} style={key === "job_notes" ? { minWidth: 240, whiteSpace: "pre-wrap" } : { whiteSpace: "nowrap" }}>
                          {row[key] === null || row[key] === "" ? "-" : String(row[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RawDataReport;
